import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject, concat, of } from 'rxjs';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';
import { ClientService } from '@app/core/client/services/client.service';
import { PipelineService } from '@app/core/pipelines/services/pipeline.service';
import { CategoryService } from '@app/core/products/services/category.service';
import { RFQ, RfqService } from '@app/core/quotes';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { StaffService } from '@app/core/staff';
import { ExportRfqService } from '@app/shared/services/export-rfq.service';
import { UIStateService } from '@app/shared/services/ui-state.service';
import { trimWala } from '@app/shared/utils/string.utils';

import { CreateClientRfqComponent } from '../../create-client-rfq/create-client-rfq.component';
import { QuotationPopupFormComponent } from '../../quotation-popup-form/quotation-popup-form.component';
import { RfqClientProfileComponent } from '../../rfq-client-profile/rfq-client-profile.component';
import { RfqFactoryAdminAssignmentComponent } from '../../rfq-factory-admin-assignment/rfq-factory-admin-assignment.component';
import { UpdateRfqLocationsComponent } from '../../update-rfq-locations/update-rfq-locations.component';
import { NpRfqPipelinePopupComponent } from '../np-rfq-pipeline-popup/np-rfq-pipeline-popup.component';

@Component({
  selector: 'cnc-np-list-view',
  templateUrl: './np-list-view.component.html',
  styleUrls: ['./np-list-view.component.scss'],
})
export class NpListViewComponent implements OnInit {
  initialize :boolean = false;
  length = 0;
  pageSize = 20;
  pageSizeOptions :number[] = [5, 10, 25, 100];
  pageTitle :string = this.translate.instant('TITLES.REQUEST_FOR_QUOTES');
  searchKey :string = '';

  filter :any = { rfq_state: '0' };
  checked :any = [];
  showFilter :boolean = false;
  checkedAll :boolean = false;
  checkedCount :number = 0;

  client$ :Observable<any[]>;
  clientLoading = false;
  clientInput$ = new Subject<string>();

  goTeam :any[] = [];
  goStaff :any[] = [];

  pipelines :any[] = [];
  rfqRecord :any;

  settings :any = {};

  constructor(
    public authService :AuthService,
    public rfqService :RfqService,
    private modalSvc :NgbModal,
    private translate :TranslateService,
    public pcatService :CategoryService,
    private router :Router,
    public exportRfq :ExportRfqService,
    public staffService :StaffService,
    public clientSvc :ClientService,
    private uiStateSvc :UIStateService,
    public settingsSvc :SettingsService,
    private toastr :ToastrService,
    private pipelineSvc :PipelineService,
    private cd :ChangeDetectorRef,
  ) {}

  params :any = {
    per_page: 20,
    page: 1,
  };

  setOrder(order :string) {
    if (this.filter.order && this.filter.order == order) {
      this.filter.order = '-' + order;
      this.filter.sort = 'desc';
    } else {
      this.filter.order = order;
      this.filter.sort = 'asc';
    }

    this.getRecords(false, 1);
  }

  ngOnInit() {
    this.settingsSvc.masterfile$.subscribe((set :any) => {
      this.settings = set;
    });

    // console.log(this.authService.loginUser);
    this.initialize = true;
    const stateView = this.uiStateSvc.getState('quotes-newproduct');
    if (stateView) {
      this.filter = stateView;
      if (!this.filter.rfq_state) {
        this.filter.rfq_state = '0';
      }
    }
    this.initialize = false;

    this.pipelineSvc.pipelines$.subscribe((pipelines) => {
      this.pipelines = pipelines;
      this.getRecords();
    });

    this.staffService.staff$.subscribe((resp) => {
      this.goTeam = resp.filter((row :any) => {
        return row.role == 2;
      });
      this.goStaff = resp.filter((row :any) => {
        return row.role != 2;
      });
    });

    this.rfqService.record$.subscribe((resp :any) => {
      this.rfqRecord = resp;
      if (resp.data) {
        resp.data.forEach((rfq :any) => {
          rfq.pipelinesObj = this.pipelineSvc.transformPipelines(rfq.pipelines);
        });
      }
      this.cd.detectChanges();
    });
  }

  editPipelines(rfq :RFQ) {
    const modalRef = this.modalSvc.open(NpRfqPipelinePopupComponent, {});
    modalRef.componentInstance.rfq = rfq;
  }

  addQuotation(rfq :RFQ) {
    this.showQuotationForm(rfq);
  }

  showQuotationForm(rfq :RFQ, quotation ?:any) {
    const modalRef = this.modalSvc.open(QuotationPopupFormComponent, {
      size: 'lg',
      windowClass: 'xl1024',
    });
    modalRef.componentInstance.rfq = rfq;
    if (quotation) {
      modalRef.componentInstance.quotation = quotation;
    }
  }

  showDetails(rfq :RFQ, v ?:string) {
    this.rfqService.setActive(rfq, true);
    if (v) {
      this.router.navigate(['inventory', rfq.id, 'view'], {
        queryParams: { v: v },
      });
    } else {
      this.router.navigate(['inventory', rfq.id, 'view']);
    }
  }

  assignSelected() {
    const rfq_ids = [];
    this.checked.forEach((v, idx) => {
      if (v) {
        rfq_ids.push(idx);
      }
    });
    const modalRef = this.modalSvc.open(RfqFactoryAdminAssignmentComponent, {});
    modalRef.componentInstance.rfq_ids = rfq_ids;
    modalRef.result.then(
      () => {
        this.checkedAll = false;
        this.checkAll();
      },
      (error) => {},
    );
  }

  gotoPurchaseOrders(rfq) {
    this.router.navigateByUrl('/orders?sku=' + rfq.internal_sku);
  }

  assignSalesMembers() {
    const rfq_ids = [];
    this.checked.forEach((v, idx) => {
      if (v) {
        rfq_ids.push(idx);
      }
    });
    const modalRef = this.modalSvc.open(RfqFactoryAdminAssignmentComponent, {});
    modalRef.componentInstance.role = 2;
    modalRef.componentInstance.rfq_ids = rfq_ids;
    modalRef.result.then(
      () => {
        this.checkedAll = false;
        this.checkAll();
      },
      (error) => {},
    );
  }

  countChecked() {
    this.checkedCount = this.checked.filter((x) => {
      return x;
    }).length;
  }

  checkAll() {
    this.rfqService.record$.subscribe((records) => {
      records.data.forEach((r) => {
        this.checked[r.id] = this.checkedAll;
      });
      this.countChecked();
    });
  }

  assignRFQ(rfq :RFQ) {
    this.rfqService.setActive(rfq, true);
    const modalRef = this.modalSvc.open(RfqFactoryAdminAssignmentComponent, {});
    modalRef.componentInstance.rfq = rfq;
  }

  viewClientInfo(rfq :RFQ) {
    this.rfqService.setActive(rfq, true);
    const modalRef = this.modalSvc.open(RfqClientProfileComponent, {});
    modalRef.componentInstance.rfq = rfq;
  }

  editRfq(rfq :RFQ) {
    const modalRef = this.modalSvc.open(CreateClientRfqComponent, {
      size: 'lg',
      windowClass: 'xl1024',
    });
    modalRef.componentInstance.rfq = rfq;
  }

  getRecordTimeout() {
    if (this.initialize) {
      return;
    }
    setTimeout(() => {
      this.getRecords(false, 1);
    }, 10);
  }

  getRecords(event ?:any, page ?:number, paramsOnly ?:boolean) {
    // let params: any = { page: 1, per_page: this.pageSize }
    if (this.initialize) {
      return;
    }
    if (page) {
      this.params.page = page;
    }

    if (this.searchKey && this.searchKey.trim().length > 0) {
      this.params.keyword = this.searchKey;
    } else {
      delete this.params.keyword;
    }

    this.params = Object.assign(this.params, this.filter);

    delete this.params.client;
    if (!this.params.sourcing || this.params.sourcing == null) {
      delete this.params.sourcing;
    }
    if (!this.params.goteam || this.params.goteam == null) {
      delete this.params.goteam;
    }

    if (!this.filter.product || this.filter.product.trim().length == 0) {
      delete this.params.product;
    }
    if (!this.filter.sku || this.filter.sku.trim().length == 0) {
      delete this.params.sku;
    }

    if (this.filter.rfq_state == 0) {
      delete this.params.rfq_state;
    }
    if (this.filter.qty_lvl == 0) {
      delete this.params.qty_lvl;
    }
    if (this.filter.rfq_dept == 0) {
      delete this.params.rfq_dept;
    }
    if (this.filter.product_type_id == 0) {
      delete this.params.product_type_id;
    }
    if (this.params.order) {
      this.params.order = trimWala(this.params.order, '-');
    }
    if (this.params.starred < 0) {
      delete this.params.starred;
    }
    if (this.filter.discontinued && this.filter.discontinued < 0) {
      delete this.params.discontinued;
    }
    // check locations
    if (this.filter.locations && this.filter.locations.length > 0) {
      this.params.locations = this.filter.locations.join(',');
    } else {
      delete this.params.locations;
    }
    if (this.filter.warehouses && this.filter.warehouses.length > 0) {
      this.params.warehouses = this.filter.warehouses.join(',');
    } else {
      delete this.params.warehouses;
    }

    if (event) {
      this.params.page = event.pageIndex + 1;
      this.params.per_page = event.pageSize;
    }

    this.params.product_type_id = 1;
    // set state
    this.uiStateSvc.setState('quotes-newproduct', this.params);

    if (paramsOnly) {
      return this.params;
    } else {
      delete this.params.all;
      this.rfqService.setParams(this.params);
      this.rfqService.refreshList();
    }
  }

  getQuoteType(id :number) {
    const qt :any = this.rfqService.quoteTypes.find((row) => {
      return row.id == id;
    });

    return qt.text;
  }

  searchRecords() {
    this.params.page = 1;

    if (this.searchKey && this.searchKey.trim().length > 0) {
      this.params.keyword = this.searchKey;
    } else {
      delete this.params.keyword;
    }

    this.params = Object.assign(this.params, this.filter);

    if (this.filter.category == 0) {
      delete this.params.category;
    }
    if (this.filter.rfq_quote_type == 0) {
      delete this.params.rfq_quote_type;
    }

    this.rfqService.setParams(this.params);
    this.rfqService.refreshList();
  }

  resetFields() {
    delete this.params.sku;
    delete this.params.date;
    delete this.params.client;
    delete this.params.product;
    delete this.params.starred;
    delete this.params.category;
    delete this.params.goteam;
    delete this.params.sourcing;
    delete this.params.rfq_quote_type;
    delete this.params.rfq_state;
    delete this.params.rfq_dept;
    delete this.params.discontinued;
    delete this.params.qty_lvl;
    delete this.params.warehouses;
    delete this.params.locations;

    this.filter = {};
    this.setOrder('created_at');
    // this.getRecords();
  }

  discontinue(rfq :RFQ) {
    this.rfqService.discontinue(rfq.id, 1).subscribe((resp :any) => {
      this.toastr.success('Successfully discontinued SKU.', 'Success');
    });
  }

  continue(rfq :RFQ) {
    this.rfqService.discontinue(rfq.id, 0).subscribe((resp :any) => {
      this.toastr.success('Successfully undiscontinued SKU.', 'Success');
    });
  }

  delete(rfq :RFQ) {
    Swal.fire({
      title: 'Confirm Delete?',
      text: 'Delete SKU for product ' + rfq.product_name + '?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.CONFIRM_DELETE_TEXT'),
    }).then((result) => {
      if (result.value) {
        this.rfqService.delete(rfq.id).subscribe((resp :any) => {
          this.toastr.success('Successfully deleted SKU.', 'Success');
        });
      }
    });
  }

  mySalesClient(userAccount) {
    if (!userAccount.go_team_member) {
      return false;
    }

    return userAccount.go_team_member.find((row) => {
      return row.staff_id == this.authService.loginUser.id;
    })
      ? true
      : false;
  }

  exportRfqToPDF(rfq :any) {
    this.exportRfq.exportRFQtoPDF(rfq, this.settings.LOGISTICS);
  }

  exportListToPdf(alternateSkus ?:boolean) {
    Swal.fire('Exporting to PDF, please wait...');
    Swal.showLoading();

    const params = Object.assign({}, this.getRecords(false, 0, true));
    delete params.page;
    delete params.per_page;
    params.all = 1;

    this.rfqService.getAsync(params).subscribe((resp) => {
      this.exportRfq.exportRfqsToPDF(resp, alternateSkus);
    });
  }

  exportListToCsv(alternateSkus ?:boolean) {
    Swal.fire('Exporting to CSV, please wait...');
    Swal.showLoading();

    const params = Object.assign({}, this.getRecords(false, 0, true));
    delete params.page;
    delete params.per_page;
    params.all = 1;

    this.rfqService.getAsync(params).subscribe((resp) => {
      if (alternateSkus) {
        this.exportRfq.exportAlternateRfqsToCsv(resp);
      } else {
        this.exportRfq.exportRfqsToCsv(resp);
      }
    });
  }

  transferRfq(rfq :any) {
    const modalRef = this.modalSvc.open(UpdateRfqLocationsComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.rfq = rfq;
  }

  updateLocations() {
    const modalRef = this.modalSvc.open(UpdateRfqLocationsComponent, {
      size: 'lg',
    });
  }

  createRfqForClient() {
    const modalRef = this.modalSvc.open(CreateClientRfqComponent, {
      size: 'lg',
      windowClass: 'xl1024',
    });
  }
}
